import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiResponseStatus } from '../../models/enums';
import { HRReviewEmployeeGoalAssignment } from '../../models/models';
import { agent } from '../../services/service';
import { CreateSubtasksRequest, UpdateSubtaskRequest, GoalReview } from '../../services/Goals';
import { toast } from 'react-toastify';

interface GoalsState {
  goalAssignments: HRReviewEmployeeGoalAssignment[];
  goalReviews: { [goalAssignmentGoalId: string]: GoalReview };
  status: string;
  error: boolean;
  loading: boolean;
  createSubtasksLoading: boolean;
  updateSubtaskLoading: boolean;
  reviewsLoading: { [goalAssignmentGoalId: string]: boolean };
}

const initialState: GoalsState = {
  goalAssignments: [],
  goalReviews: {},
  status: '',
  error: false,
  loading: false,
  createSubtasksLoading: false,
  updateSubtaskLoading: false,
  reviewsLoading: {}
};

export const getEmployeeGoals = createAsyncThunk(
  'goals/getEmployeeGoals',
  async (employeeId: string) => {
    try {
      const response = await agent.GoalsService.getEmployeeGoals(employeeId);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee goals:', error);
      toast.error('Failed to fetch goals. Please try again.');
      throw error;
    }
  }
);

export const createSubtasks = createAsyncThunk(
  'goals/createSubtasks',
  async ({ goalAssignmentGoalId, data }: { goalAssignmentGoalId: string; data: CreateSubtasksRequest }) => {
    try {
      const response = await agent.GoalsService.createSubtasks(goalAssignmentGoalId, data);
      toast.success('Subtasks created successfully!');
      return response.data;
    } catch (error) {
      console.error('Error creating subtasks:', error);
      toast.error('Failed to create subtasks. Please try again.');
      throw error;
    }
  }
);

export const updateSubtask = createAsyncThunk(
  'goals/updateSubtask',
  async ({ subtaskId, data }: { subtaskId: string; data: UpdateSubtaskRequest }) => {
    try {
      await agent.GoalsService.updateSubtask(subtaskId, data);
      toast.success('Subtask updated successfully!');
      return { subtaskId, updatedData: data };
    } catch (error) {
      console.error('Error updating subtask:', error);
      toast.error('Failed to update subtask. Please try again.');
      throw error;
    }
  }
);

export const deleteSubtask = createAsyncThunk(
  'goals/deleteSubtask',
  async (subtaskId: string) => {
    try {
      await agent.GoalsService.deleteSubtask(subtaskId);
      toast.success('Subtask deleted successfully!');
      return subtaskId;
    } catch (error) {
      console.error('Error deleting subtask:', error);
      toast.error('Failed to delete subtask. Please try again.');
      throw error;
    }
  }
);

export const getGoalReviews = createAsyncThunk(
  'goals/getGoalReviews',
  async (goalAssignmentGoalId: string) => {
    try {
      const response = await agent.GoalsService.getGoalReviews(goalAssignmentGoalId);
      return { goalAssignmentGoalId, review: response.data };
    } catch (error) {
      console.error('Error fetching goal reviews:', error);
      // Don't show error toast for reviews as they might not exist
      throw error;
    }
  }
);

export const acceptGoalAssignment = createAsyncThunk(
  'goals/acceptGoalAssignment',
  async ({ goalAssignmentGoalId, status }: { goalAssignmentGoalId: string; status: string }, { rejectWithValue }) => {
    try {
      await agent.GoalsService.acceptGoalAssignment(goalAssignmentGoalId, status);
      const message = status === 'Accepted' ? 'Goal accepted successfully!' : 'Goal declined successfully!';
      toast.success(message);
      return { goalAssignmentGoalId, status };
    } catch (error) {
      const message = status === 'Accepted' ? 'Failed to accept goal. Please try again.' : 'Failed to decline goal. Please try again.';
      toast.error(message);
      return rejectWithValue(error);
    }
  }
);

export const updateGoalStatus = createAsyncThunk(
  'goals/updateGoalStatus',
  async ({ goalAssignmentGoalId, status }: { goalAssignmentGoalId: string; status: string }, { rejectWithValue }) => {
    try {
      await agent.GoalsService.updateGoalStatus(goalAssignmentGoalId, status);
      toast.success('Goal status updated successfully!');
      return { goalAssignmentGoalId, status };
    } catch (error) {
      console.error('Error updating goal status:', error);
      toast.error('Failed to update goal status. Please try again.');
      return rejectWithValue(error);
    }
  }
);

const goalsSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    clearGoalsState: (state) => ({ ...state, ...initialState })
  },
  extraReducers: (builder) => {
    // Get Employee Goals
    builder.addCase(getEmployeeGoals.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
      state.loading = true;
    });

    builder.addCase(getEmployeeGoals.fulfilled, (state, { payload }) => {
      state.goalAssignments = payload;
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
      state.loading = false;
    });

    builder.addCase(getEmployeeGoals.rejected, (state) => {
      state.goalAssignments = [];
      state.status = ApiResponseStatus.rejected;
      state.error = true;
      state.loading = false;
    });

    // Create Subtasks
    builder.addCase(createSubtasks.pending, (state) => {
      state.createSubtasksLoading = true;
    });

    builder.addCase(createSubtasks.fulfilled, (state) => {
      state.createSubtasksLoading = false;
    });

    builder.addCase(createSubtasks.rejected, (state) => {
      state.createSubtasksLoading = false;
    });

    // Update Subtask
    builder.addCase(updateSubtask.pending, (state) => {
      state.updateSubtaskLoading = true;
    });

    builder.addCase(updateSubtask.fulfilled, (state) => {
      state.updateSubtaskLoading = false;
    });

    builder.addCase(updateSubtask.rejected, (state) => {
      state.updateSubtaskLoading = false;
    });

    // Delete Subtask
    builder.addCase(deleteSubtask.pending, (state) => {
      state.updateSubtaskLoading = true;
    });

    builder.addCase(deleteSubtask.fulfilled, (state) => {
      state.updateSubtaskLoading = false;
    });

    builder.addCase(deleteSubtask.rejected, (state) => {
      state.updateSubtaskLoading = false;
    });

    // Get Goal Reviews
    builder.addCase(getGoalReviews.pending, (state, { meta }) => {
      state.reviewsLoading[meta.arg] = true;
    });

    builder.addCase(getGoalReviews.fulfilled, (state, { payload }) => {
      state.goalReviews[payload.goalAssignmentGoalId] = payload.review;
      state.reviewsLoading[payload.goalAssignmentGoalId] = false;
    });

    builder.addCase(getGoalReviews.rejected, (state, { meta }) => {
      state.reviewsLoading[meta.arg] = false;
    });


    builder.addCase(acceptGoalAssignment.fulfilled, (state, { payload }) => {
      state.goalAssignments = state.goalAssignments.map(assignment => ({
        ...assignment,
        goals: assignment.goals.map(goal => 
          goal.id === payload.goalAssignmentGoalId 
            ? { ...goal, acceptanceStatus: payload.status }
            : goal
        )
      }));
    });


    builder.addCase(updateGoalStatus.fulfilled, (state, { payload }) => {
      state.goalAssignments = state.goalAssignments.map(assignment => ({
        ...assignment,
        goals: assignment.goals.map(goal => 
          goal.id === payload.goalAssignmentGoalId 
            ? { ...goal, status: payload.status as 'Pending' | 'In Progress' | 'Completed' }
            : goal
        )
      }));
    });
  }
});

const { actions, reducer } = goalsSlice;

export const { clearGoalsState } = actions;

export default reducer; 